import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
    Box, Typography, Stack, Chip, FormControl, Select, MenuItem, Pagination,
    Table, TableBody, TableContainer, TableHead, TableRow, Paper, Button,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getLabAnalyzerLogsRequest, getByIdLabAnalyzerRequest, resetGetByIdLabAnalyzer, resetGetLabAnalyzerLogs } from '../../../../redux/modules/laboratory/labAnalyzer/slice';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: { backgroundColor: theme.palette.primary.main, color: theme.palette.common.white },
    [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
    '&:last-child td, &:last-child th': { border: 0 },
}));

const eventTypeColors = {
    connection: 'success', disconnection: 'warning', message_sent: 'info',
    message_received: 'primary', result_parsed: 'secondary', error: 'error', worklist_sent: 'info',
};

const directionLabels = { inbound: 'IN', outbound: 'OUT', internal: 'INT' };

let limit = 25;

export default function LabAnalyzerLogs() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = '/' + location.pathname.split('/')[1];
    const { id } = useParams();

    const { getByIdRes, getLogsRes } = useSelector(state => state.labAnalyzer);
    const [eventFilter, setEventFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rawDataDialog, setRawDataDialog] = useState({ open: false, data: '' });

    useEffect(() => {
        dispatch(setMainHeading("Communication Logs"));
        dispatch(getByIdLabAnalyzerRequest(id));
        return () => {
            dispatch(resetGetByIdLabAnalyzer());
            dispatch(resetGetLabAnalyzerLogs());
        };
    }, [dispatch, id]);

    useEffect(() => {
        dispatch(getLabAnalyzerLogsRequest({ id, page: currentPage, limit, eventType: eventFilter }));
    }, [dispatch, id, currentPage, eventFilter]);

    const pageCount = getLogsRes?.dataCount > limit ? Math.ceil(getLogsRes.dataCount / limit) : 0;

    return (
        <>
            <Stack direction="row" alignItems="center" spacing={2} mb={3} mt={4}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`${basePath}/laboratory/analyzers`)} size="small">
                    Back
                </Button>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Logs: {getByIdRes?.data?.name || 'Loading...'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {getLogsRes?.dataCount || 0} log entries
                    </Typography>
                </Box>
            </Stack>

            <Stack mb={2} direction="row" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <Select value={eventFilter} onChange={(e) => { setEventFilter(e.target.value); setCurrentPage(1); }} displayEmpty>
                        <MenuItem value="">All Events</MenuItem>
                        {['connection', 'disconnection', 'message_sent', 'message_received', 'result_parsed', 'error', 'worklist_sent'].map(et => (
                            <MenuItem key={et} value={et} sx={{ textTransform: 'capitalize' }}>{et.replace(/_/g, ' ')}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
                <TableContainer component={Paper} className="tableShadow">
                    <Table sx={{ minWidth: { xs: 500, md: 800 } }} className="commonTable">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Time</StyledTableCell>
                                <StyledTableCell>Event</StyledTableCell>
                                <StyledTableCell>Direction</StyledTableCell>
                                <StyledTableCell>Accession #</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>Details</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getLogsRes?.data?.length > 0 ? getLogsRes.data.map(log => (
                                <StyledTableRow key={log._id}>
                                    <StyledTableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {log.createdAt ? new Date(log.createdAt).toLocaleString() : '-'}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip
                                            label={log.eventType?.replace(/_/g, ' ')}
                                            size="small"
                                            color={eventTypeColors[log.eventType] || 'default'}
                                            sx={{ textTransform: 'capitalize' }}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip label={directionLabels[log.direction] || log.direction} size="small" variant="outlined" />
                                    </StyledTableCell>
                                    <StyledTableCell>{log.accessionNumber || '-'}</StyledTableCell>
                                    <StyledTableCell>
                                        <Chip
                                            label={log.success ? 'OK' : 'FAIL'}
                                            size="small"
                                            color={log.success ? 'success' : 'error'}
                                            variant="outlined"
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {log.errorMessage && (
                                            <Typography variant="caption" color="error.main">{log.errorMessage}</Typography>
                                        )}
                                        {log.rawData && (
                                            <Button size="small" onClick={() => setRawDataDialog({ open: true, data: log.rawData })}>
                                                View Raw
                                            </Button>
                                        )}
                                        {log.parsedData && !log.rawData && (
                                            <Button size="small" onClick={() => setRawDataDialog({ open: true, data: JSON.stringify(log.parsedData, null, 2) })}>
                                                View Parsed
                                            </Button>
                                        )}
                                    </StyledTableCell>
                                </StyledTableRow>
                            )) : (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={6}>
                                        <Typography color="text.secondary">No log entries found</Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {pageCount > 0 && (
                <Stack direction="row" justifyContent="center" mt={2}>
                    <Pagination count={pageCount} page={currentPage} onChange={(e, v) => setCurrentPage(v)} />
                </Stack>
            )}

            <Dialog open={rawDataDialog.open} onClose={() => setRawDataDialog({ open: false, data: '' })} maxWidth="md" fullWidth>
                <DialogTitle>Raw Data</DialogTitle>
                <DialogContent>
                    <Box sx={{ fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-all', p: 2, bgcolor: '#f5f5f5', borderRadius: 1, maxHeight: 400, overflow: 'auto' }}>
                        {rawDataDialog.data}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRawDataDialog({ open: false, data: '' })}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
